/**
 * A shared ticking clock.
 *
 * Countdowns are everywhere in this product: a customer watching their order, a
 * merchant watching twenty of them. Giving each component its own `setInterval`
 * would mean twenty timers and twenty independent re-render schedules, which is
 * both wasteful and visibly jittery — two orders placed a second apart would
 * appear to tick out of step. One provider per app keeps every countdown on the
 * same frame.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

const ClockContext = createContext<Date | null>(null);

/**
 * Provides a shared, ticking "now" to every descendant.
 *
 * @param props Tick period and children.
 */
export function ClockProvider({
  children,
  intervalMs = 1000,
}: {
  children: ReactNode;
  intervalMs?: number;
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  // `now` is already a fresh object once per tick, so there is nothing to
  // memoise: every consumer should re-render on the same frame.
  return <ClockContext.Provider value={now}>{children}</ClockContext.Provider>;
}

/**
 * The current instant, refreshed on the shared tick.
 *
 * Falls back to a private timer when no provider is mounted, so a component can
 * be rendered in isolation — in a test or a story — without extra setup.
 *
 * @param intervalMs Tick period used only by the fallback.
 * @returns The current instant.
 */
export function useClock(intervalMs = 1000): Date {
  const shared = useContext(ClockContext);
  const [local, setLocal] = useState(() => new Date());

  useEffect(() => {
    if (shared) return;
    const id = setInterval(() => setLocal(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [shared, intervalMs]);

  return shared ?? local;
}

/**
 * A ticking clock for countdowns.
 *
 * @param intervalMs Tick period; one second suits every countdown in the app.
 * @returns The current instant, updated on each tick.
 */
export const useNow = useClock;
